import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import posthog from "@/posthog";
import AuthModal from "@/components/auth/AuthModal";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const login = () => {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      // Expected when not signed in (/auth/me returns 401) — treat as anonymous.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Identify the person in PostHog whenever a signed-in user is present.
  useEffect(() => {
    if (user?.user_id) {
      // Privacy: identify by opaque id only; keep role for segmentation, omit email/name (PII).
      posthog?.identify(user.user_id, { role: user.role });
    }
  }, [user]);

  const openAuth = useCallback(() => setAuthOpen(true), []);
  const closeAuth = useCallback(() => setAuthOpen(false), []);

  const emailLogin = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data);
    posthog?.capture("user_signed_in", { method: "email" });
    return res.data;
  }, []);

  const emailRegister = useCallback(async (name, email, password) => {
    const res = await api.post("/auth/register", { name, email, password });
    setUser(res.data);
    posthog?.capture("user_signed_up", { method: "email" });
    return res.data;
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Best-effort: even if the server call fails, clear the local session.
      console.debug("Logout request failed; clearing local session anyway.", err);
    }
    setUser(null);
    posthog?.reset(); // start a fresh anonymous person for the next user
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, refresh: checkAuth, logout, authOpen, openAuth, closeAuth, emailLogin, emailRegister }}
    >
      {children}
      <AuthModal />
    </AuthContext.Provider>
  );
};
